import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { PdfExportSettings, UserRef } from '../api/types';
import { PdfExportSettingsModal, DEFAULT_SETTINGS as DEFAULT_PDF_SETTINGS } from './PdfExportSettingsModal';
import { generateDiagramPdf } from '../utils/pdfExport';
import { useAuth } from '../contexts/AuthContext';

/**
 * Carte "nom du diagramme source + export PDF direct 📄 + réglages 🖨️",
 * réutilisée à l'identique par SynthesisPage et WorkloadPage (demande
 * explicite d'homogénéiser les deux) — ancrée sur CE diagramme source
 * précis, réutilise `generateDiagramPdf`/`PdfExportSettingsModal` tels
 * quels (mêmes réglages persistés que la page du diagramme lui-même).
 */
export function SourceDiagramExportCard({
  diagramId,
  diagramName,
  assignee,
}: {
  diagramId: string;
  diagramName: string;
  /** Personne concernée par la synthèse en cours (voir SynthesisGroup.assignee), affichée à côté du nom. */
  assignee?: UserRef | null;
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    if (exporting) return;
    setError(null);
    setExporting(true);
    try {
      const saved = await api.get<PdfExportSettings | null>(`/diagrams/${diagramId}/pdf-export-settings`);
      await generateDiagramPdf({
        diagramId,
        diagramName,
        settings: { ...DEFAULT_PDF_SETTINGS, ...saved },
        dateFormat: user?.dateFormat ?? 'DD-MM-YYYY',
        weekNumbering: user?.weekNumbering ?? 'ISO',
        locale: i18n.language,
        weekAbbrev: (n: number) => t('chart.weekAbbrev', { n }),
        columnLabel: (col: string) => t(`tasks.colShort.${col}`),
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ marginBottom: 4 }}>
      <div className="card diagram-toolbar" style={{ marginBottom: 4 }}>
        <strong className="diagram-name-display">
          <Link to={`/diagrams/${diagramId}`}>{diagramName}</Link>
          {assignee && (
            <span className="synthesis-assignee">
              {assignee.firstName} {assignee.lastName}
            </span>
          )}
        </strong>
        <button
          className="btn icon-btn"
          onClick={handleExport}
          disabled={exporting}
          title={t(exporting ? 'diagram.exporting' : 'diagram.exportPdf') as string}
        >
          📄
        </button>
        <button className="btn icon-btn" onClick={() => setShowSettings(true)} title={t('pdfExport.title') as string}>
          🖨️
        </button>
      </div>
      {error && <p className="error-text">{error}</p>}
      {showSettings && <PdfExportSettingsModal diagramId={diagramId} canWrite={false} onClose={() => setShowSettings(false)} />}
    </div>
  );
}
